import { api, type Page } from "@/lib/api/client";
import type { AttachmentPurpose, UploadIntent } from "@/features/directory/api/directory.types";
import type {
  Assignment,
  AssignmentListQuery,
  CompleteServiceInput,
  Connection,
  ConnectionTest,
  CostReport,
  CostReportQuery,
  CreateAssignmentInput,
  CreateConnectionInput,
  CreateDamageInput,
  CreateDocumentInput,
  CreateExpenseInput,
  CreateFinancingInput,
  CreateFineInput,
  CreateFleetVehicleInput,
  CreateFuelInput,
  CreateIdentifierInput,
  CreateImportInput,
  CreateOdometerInput,
  CreatePlanInput,
  CreateReportExportInput,
  CreateServiceInput,
  CreateTollInput,
  Damage,
  DamageStatusInput,
  Eligibility,
  Expense,
  ExpenseListQuery,
  Financing,
  FineListQuery,
  FleetAttachment,
  FleetDashboard,
  FleetPerson,
  FleetSubject,
  FleetVehicle,
  FleetVehicleListQuery,
  FuelEntry,
  FuelListQuery,
  ImportBatch,
  ImportPreview,
  ImportPreviewInput,
  LastPosition,
  MaintenancePlan,
  MapConfig,
  MatchInput,
  MyVehicle,
  OdometerReading,
  PayFineInput,
  ProviderInfo,
  Recognition,
  RecognitionKind,
  RecourseInput,
  ReportExportJob,
  ReturnAssignmentInput,
  ReturnPreview,
  ServiceRecord,
  TimelineEntry,
  TollListQuery,
  TollPass,
  TrackPoint,
  TrafficFine,
  UpdateConnectionInput,
  UpdateDocumentInput,
  UpdateExpenseInput,
  UpdateFleetVehicleInput,
  UpdateFuelInput,
  UpdatePlanInput,
  VehicleDocument,
  VehicleIdentifier,
} from "./fleet.types";
import type { FleetCalendar, FleetDueView, FleetDueWindow, HolderAt, VehicleRemoval, VehicleSuggestions, VehicleSummary } from "./fleet.types";
import type {
  CloseDamageInput,
  CorrectPlateInput,
  CreateTaxInput,
  FleetCostCenter,
  FuelConsumption,
  PayTaxInput,
  UpdateTaxInput,
  VehicleTax,
} from "./fleet.types";

/**
 * Filo uçları (`/api/v1/fleet/*`). Her yazma kendi ucunda: durum değişikliği (iade, ödeme, servis
 * tamamlama) bir alan düzeltmesi değil, kendi kuralı ve kendi kaydı olan bir olay.
 *
 * PARA ÖDEYEN ÜÇ UÇ İDEMPOTENT (`Idempotency-Key`): ceza ödemesi, taksit ödemesi, erken kapama.
 * Ağ kopup istek yeniden gönderilirse ödeme iki kez yazılmamalı — anahtar mutasyon başına bir kez
 * üretilir ve yeniden denemede aynı kalır.
 *
 * YETKİ AİLESİNE GÖRE AYRI NESNELER (7 Ekim 2026). Tek bir `fleetApi` vardı ve `check:screen-access`
 * bir sayfanın içe aktardığı API nesnesinin BÜTÜN okuma uçlarını o sayfanın ihtiyacı sayıyor
 * (bilerek güvenli tarafta). Sonuç: araç kayıt sihirbazı KRİTİK `fleet.location.read` istiyor
 * görünüyordu. Kalıp depoda zaten var (`eventsApi` / `eventPlansApi` / `physiciansApi`).
 */

type Params = Record<string, string | number | boolean | null | undefined>;
const clean = <T extends object>(query: T): Params => ({ ...(query as Params) });

/** Genel bakış ve «Aracım» (`fleet.vehicle.read`; kendi aracı SELF `fleet.assignment.read`). */
export const fleetOverviewApi = {
  dashboard(legalEntityId: string | null): Promise<FleetDashboard> {
    return api.get<FleetDashboard>("/fleet/dashboard", { params: { legalEntityId } });
  },
  me(): Promise<MyVehicle> {
    return api.get<MyVehicle>("/fleet/me");
  },
  /** Filo takvimi: aralıktaki vadeler ve zimmet olayları (en çok 92 gün). */
  calendar(params: { legalEntityId: string | null; from: string; to: string }): Promise<FleetCalendar> {
    return api.get<FleetCalendar>("/fleet/calendar", { params });
  },
};

/** Araç kartı: kayıt, kimlikler, zaman çizelgesi, km, belge (`fleet.vehicle.*`). */
export const fleetVehicleApi = {
  vehicles(query: FleetVehicleListQuery): Promise<Page<FleetVehicle>> {
    return api.page<FleetVehicle>("/fleet/vehicles", { params: clean(query) });
  },
  vehicle(id: string): Promise<FleetVehicle> {
    return api.get<FleetVehicle>(`/fleet/vehicles/${id}`);
  },
  createVehicle(input: CreateFleetVehicleInput): Promise<FleetVehicle> {
    return api.post<FleetVehicle>("/fleet/vehicles", input);
  },
  updateVehicle(id: string, input: UpdateFleetVehicleInput): Promise<FleetVehicle> {
    return api.patch<FleetVehicle>(`/fleet/vehicles/${id}`, input);
  },
  /** Plaka düzeltme (F-13): gerekçe zorunlu, aracın geçmişine yazılır. PATCH plaka almıyor. */
  correctPlate(id: string, input: CorrectPlateInput): Promise<FleetVehicle> {
    return api.post<FleetVehicle>(`/fleet/vehicles/${id}/plate`, input);
  },
  removeVehicle(id: string, version: number): Promise<void> {
    return api.delete(`/fleet/vehicles/${id}`, { params: { version } });
  },
  /** Silinebilir mi, değilse neden (geçmişi olan araç silinmez) — «Sil» penceresi bunu okur. */
  vehicleRemoval(id: string): Promise<VehicleRemoval> {
    return api.get<VehicleRemoval>(`/fleet/vehicles/${id}/removal`);
  },
  /** Kayıt ve filtre önerileri: filoda kullanılan marka → modeller, bölgeler, renkler, kasa tipleri. */
  suggestions(legalEntityId: string | null): Promise<VehicleSuggestions> {
    return api.get<VehicleSuggestions>("/fleet/vehicles/suggestions", { params: { legalEntityId } });
  },
  /** O an kimdeydi — ceza girerken ihlal anının zimmetlisi (`GET /fleet/vehicles/{id}/holder`). */
  holderAt(id: string, at: string): Promise<HolderAt> {
    return api.get<HolderAt>(`/fleet/vehicles/${id}/holder`, { params: { at } });
  },
  /** «Vadeler»: geciken ve yaklaşan vadeler + belgesi eksik araçlar. */
  due(params: { legalEntityId: string | null; within: FleetDueWindow }): Promise<FleetDueView> {
    return api.get<FleetDueView>("/fleet/vehicles/due", { params });
  },
  identifiers(id: string): Promise<VehicleIdentifier[]> {
    return api.get<VehicleIdentifier[]>(`/fleet/vehicles/${id}/identifiers`);
  },
  addIdentifier(id: string, input: CreateIdentifierInput): Promise<VehicleIdentifier> {
    return api.post<VehicleIdentifier>(`/fleet/vehicles/${id}/identifiers`, input);
  },
  endIdentifier(id: string, identifierId: string, input: { validTo: string; version: number }): Promise<VehicleIdentifier> {
    return api.post<VehicleIdentifier>(`/fleet/vehicles/${id}/identifiers/${identifierId}/end`, input);
  },
  timeline(id: string): Promise<TimelineEntry[]> {
    return api.get<TimelineEntry[]>(`/fleet/vehicles/${id}/timeline`);
  },
  summary(id: string): Promise<VehicleSummary> {
    return api.get<VehicleSummary>(`/fleet/vehicles/${id}/summary`);
  },
  odometer(fleetVehicleId: string): Promise<Page<OdometerReading>> {
    return api.page<OdometerReading>("/fleet/odometer-readings", { params: { fleetVehicleId, limit: 50, sortBy: "readAt", sortOrder: "desc" } });
  },
  addOdometer(input: CreateOdometerInput): Promise<OdometerReading> {
    return api.post<OdometerReading>("/fleet/odometer-readings", input);
  },
  /** Yalnız elle girilmiş, zimmetin teslim/iade okuması olmayan okuma (sunucu kuralı). */
  removeOdometer(id: string): Promise<void> {
    return api.delete(`/fleet/odometer-readings/${id}`);
  },
  documents(fleetVehicleId: string): Promise<Page<VehicleDocument>> {
    return api.page<VehicleDocument>("/fleet/documents", { params: { fleetVehicleId, limit: 100, sortBy: "endsOn", sortOrder: "desc" } });
  },
  document(id: string): Promise<VehicleDocument> {
    return api.get<VehicleDocument>(`/fleet/documents/${id}`);
  },
  addDocument(input: CreateDocumentInput): Promise<VehicleDocument> {
    return api.post<VehicleDocument>("/fleet/documents", input);
  },
  updateDocument(id: string, input: UpdateDocumentInput): Promise<VehicleDocument> {
    return api.patch<VehicleDocument>(`/fleet/documents/${id}`, input);
  },
  /** Gerekçe GÖVDEDE (sorgu dizesinde değil) — denetim kaydına yazılır. */
  removeDocument(id: string, version: number, reason: string): Promise<void> {
    return api.delete(`/fleet/documents/${id}`, { params: { version }, body: { reason } });
  },
};

/** Zimmet: teslim, iade, uygunluk, kişi seçici (`fleet.assignment.*`). */
export const fleetAssignmentApi = {
  assignments(query: AssignmentListQuery): Promise<Page<Assignment>> {
    return api.page<Assignment>("/fleet/assignments", { params: clean(query) });
  },
  assignment(id: string): Promise<Assignment> {
    return api.get<Assignment>(`/fleet/assignments/${id}`);
  },
  /** Zimmet/eşleme seçicisi: ad, sicil no — İK okuma yetkisi gerektirmeyen dar uç. */
  /** `vehicle`: zimmet sihirbazı — `WITHOUT` araçsızlar (yeni teslim), `WITH` araçlılar (takas). */
  people(params: { q?: string; legalEntityId?: string | null; limit?: number; vehicle?: "WITHOUT" | "WITH" }): Promise<FleetPerson[]> {
    return api.get<FleetPerson[]>("/fleet/assignments/people", { params });
  },
  /**
   * `licenceClasses` + `licenceExpiresOn` (31 Ekim 2026): sihirbazda girilmekte olan ehliyet — denetim onunla
   * yapılır, kayda geçmez; kişinin kartına teslim (`driverLicence`) yazar.
   */
  eligibility(params: {
    fleetVehicleId: string;
    employmentId: string;
    at?: string;
    licenceClasses?: string;
    licenceExpiresOn?: string;
    /** Takasta iade alınacak zimmet — «kişide başka araç var» uyarısına sayılmaz. */
    swapAssignmentId?: string;
  }): Promise<Eligibility> {
    return api.get<Eligibility>("/fleet/assignments/eligibility", { params });
  },
  handOver(input: CreateAssignmentInput): Promise<Assignment> {
    return api.post<Assignment>("/fleet/assignments", input);
  },
  returnPreview(id: string, params: { returnedAt?: string; endKm?: number }): Promise<ReturnPreview> {
    return api.get<ReturnPreview>(`/fleet/assignments/${id}/return-preview`, { params });
  },
  returnVehicle(id: string, input: ReturnAssignmentInput): Promise<Assignment> {
    return api.post<Assignment>(`/fleet/assignments/${id}/return`, input);
  },
  /**
   * Teslim / iade tutanağı PDF'i (F-24, 20 Ekim 2026) — JSON zarfı yok; baytlar okunur ki 403/404/503
   * boş bir sekme değil, ekranın söyleyebildiği bir hata olsun (`getBlob`'un gerekçesi). Kapı zimmet
   * okuma; sürücü SELF ile kendi tutanağını alır.
   */
  protocol(id: string, kind: "HANDOVER" | "RETURN"): Promise<Blob> {
    return api.getBlob(`/fleet/assignments/${encodeURIComponent(id)}/protocol?kind=${kind}`, "application/pdf");
  },
};

/** Trafik cezası ve HGS geçişi (`fleet.fine.*`). */
export const fleetFineApi = {
  fines(query: FineListQuery): Promise<Page<TrafficFine>> {
    return api.page<TrafficFine>("/fleet/fines", { params: clean(query) });
  },
  fine(id: string): Promise<TrafficFine> {
    return api.get<TrafficFine>(`/fleet/fines/${id}`);
  },
  addFine(input: CreateFineInput): Promise<TrafficFine> {
    return api.post<TrafficFine>("/fleet/fines", input);
  },
  payFine(id: string, input: PayFineInput, idempotencyKey: string): Promise<TrafficFine> {
    return api.post<TrafficFine>(`/fleet/fines/${id}/pay`, input, { idempotencyKey });
  },
  /** Ödemeyi geri al (F-9): gerekçe zorunlu; ceza «Ödenmedi»ye döner, masraf satırı düşer. */
  unpayFine(id: string, input: { reason: string; version: number }): Promise<TrafficFine> {
    return api.post<TrafficFine>(`/fleet/fines/${id}/unpay`, input);
  },
  setFineStatus(id: string, input: { status: "DISPUTED" | "CANCELLED" | "PENDING"; note?: string; version: number }): Promise<TrafficFine> {
    return api.post<TrafficFine>(`/fleet/fines/${id}/status`, input);
  },
  setRecourse(id: string, input: RecourseInput): Promise<TrafficFine> {
    return api.post<TrafficFine>(`/fleet/fines/${id}/recourse`, input);
  },
  matchFine(id: string, input: MatchInput): Promise<TrafficFine> {
    return api.post<TrafficFine>(`/fleet/fines/${id}/match`, input);
  },
  tolls(query: TollListQuery): Promise<Page<TollPass>> {
    return api.page<TollPass>("/fleet/toll-passes", { params: clean(query) });
  },
  toll(id: string): Promise<TollPass> {
    return api.get<TollPass>(`/fleet/toll-passes/${id}`);
  },
  addToll(input: CreateTollInput): Promise<TollPass> {
    return api.post<TollPass>("/fleet/toll-passes", input);
  },
  matchToll(id: string, input: MatchInput): Promise<TollPass> {
    return api.post<TollPass>(`/fleet/toll-passes/${id}/match`, input);
  },
};

/** Yakıt, masraf defteri, taksit, maliyet raporu (`fleet.cost.*`). */
export const fleetCostApi = {
  fuel(query: FuelListQuery): Promise<Page<FuelEntry>> {
    return api.page<FuelEntry>("/fleet/fuel-entries", { params: clean(query) });
  },
  fuelEntry(id: string): Promise<FuelEntry> {
    return api.get<FuelEntry>(`/fleet/fuel-entries/${id}`);
  },
  addFuel(input: CreateFuelInput): Promise<FuelEntry> {
    return api.post<FuelEntry>("/fleet/fuel-entries", input);
  },
  updateFuel(id: string, input: UpdateFuelInput): Promise<FuelEntry> {
    return api.patch<FuelEntry>(`/fleet/fuel-entries/${id}`, input);
  },
  /** Masraf defterinden düşer, km okuması km günlüğünden çıkar; gerekçe gövdede zorunlu. */
  removeFuel(id: string, version: number, reason: string): Promise<void> {
    return api.delete(`/fleet/fuel-entries/${id}`, { params: { version }, body: { reason } });
  },
  matchFuel(id: string, input: MatchInput): Promise<FuelEntry> {
    return api.post<FuelEntry>(`/fleet/fuel-entries/${id}/match`, input);
  },
  expenses(query: ExpenseListQuery): Promise<Page<Expense>> {
    return api.page<Expense>("/fleet/expenses", { params: clean(query) });
  },
  addExpense(input: CreateExpenseInput): Promise<Expense> {
    return api.post<Expense>("/fleet/expenses", input);
  },
  /** Yalnız elle girilmiş masraf; kaynaktan doğan satır kaynağından düzeltilir. */
  updateExpense(id: string, input: UpdateExpenseInput): Promise<Expense> {
    return api.patch<Expense>(`/fleet/expenses/${id}`, input);
  },
  removeExpense(id: string, version: number, reason: string): Promise<void> {
    return api.delete(`/fleet/expenses/${id}`, { params: { version }, body: { reason } });
  },
  financings(params: Params): Promise<Page<Financing>> {
    return api.page<Financing>("/fleet/financings", { params });
  },
  financing(id: string): Promise<Financing> {
    return api.get<Financing>(`/fleet/financings/${id}`);
  },
  addFinancing(input: CreateFinancingInput, idempotencyKey: string): Promise<Financing> {
    return api.post<Financing>("/fleet/financings", input, { idempotencyKey });
  },
  payInstallment(id: string, seq: number, input: { paidOn: string; paidAmount?: string; version: number }, idempotencyKey: string): Promise<Financing> {
    return api.post<Financing>(`/fleet/financings/${id}/installments/${seq}/pay`, input, { idempotencyKey });
  },
  settleFinancing(id: string, input: { settledOn: string; settlementAmount: string; version: number }, idempotencyKey: string): Promise<Financing> {
    return api.post<Financing>(`/fleet/financings/${id}/settle`, input, { idempotencyKey });
  },
  costReport(query: CostReportQuery): Promise<CostReport> {
    return api.get<CostReport>("/fleet/reports/costs", { params: clean(query) });
  },
  /** Ortalama tüketim ve litre fiyatı (F-6); dönem verilmezse son 12 ay. */
  consumption(params: { fleetVehicleId: string; from?: string; to?: string }): Promise<FuelConsumption> {
    return api.get<FuelConsumption>("/fleet/fuel-entries/consumption", { params });
  },
  /** Masraf formunun masraf merkezi seçicisi (dar uç, `fleet.cost.manage`). */
  costCenters(legalEntityId: string | null): Promise<FleetCostCenter[]> {
    return api.get<FleetCostCenter[]>("/fleet/expenses/cost-centers", { params: { legalEntityId } });
  },
  /** MTV yükümlülüğü (20 Ekim 2026). */
  /** `from`/`to` son ödeme gününe göre (31 Ekim 2026: zimmet sayfası o zimmetin süresine düşen MTV'yi sorar). */
  taxes(params: { fleetVehicleId: string; limit?: number; from?: string; to?: string }): Promise<Page<VehicleTax>> {
    return api.page<VehicleTax>("/fleet/taxes", { params: { limit: 50, sortBy: "dueOn", sortOrder: "desc", ...params } });
  },
  addTax(input: CreateTaxInput): Promise<VehicleTax> {
    return api.post<VehicleTax>("/fleet/taxes", input);
  },
  updateTax(id: string, input: UpdateTaxInput): Promise<VehicleTax> {
    return api.patch<VehicleTax>(`/fleet/taxes/${id}`, input);
  },
  payTax(id: string, input: PayTaxInput, idempotencyKey: string): Promise<VehicleTax> {
    return api.post<VehicleTax>(`/fleet/taxes/${id}/pay`, input, { idempotencyKey });
  },
  unpayTax(id: string, input: { reason: string; version: number }): Promise<VehicleTax> {
    return api.post<VehicleTax>(`/fleet/taxes/${id}/unpay`, input);
  },
  removeTax(id: string, version: number, reason: string): Promise<void> {
    return api.delete(`/fleet/taxes/${id}`, { params: { version }, body: { reason } });
  },
};

/** Bakım planı, servis kaydı, hasar (`fleet.maintenance.*`). */
export const fleetMaintenanceApi = {
  plans(params: { fleetVehicleId?: string; legalEntityId?: string | null; limit?: number }): Promise<Page<MaintenancePlan>> {
    return api.page<MaintenancePlan>("/fleet/maintenance-plans", { params: { limit: 100, ...params } });
  },
  addPlan(input: CreatePlanInput): Promise<MaintenancePlan> {
    return api.post<MaintenancePlan>("/fleet/maintenance-plans", input);
  },
  /** Düzeltme ve pasife alma (`active: false`) aynı uçtan. */
  updatePlan(id: string, input: UpdatePlanInput): Promise<MaintenancePlan> {
    return api.patch<MaintenancePlan>(`/fleet/maintenance-plans/${id}`, input);
  },
  removePlan(id: string, version: number): Promise<void> {
    return api.delete(`/fleet/maintenance-plans/${id}`, { params: { version } });
  },
  services(params: Params): Promise<Page<ServiceRecord>> {
    return api.page<ServiceRecord>("/fleet/service-records", { params });
  },
  service(id: string): Promise<ServiceRecord> {
    return api.get<ServiceRecord>(`/fleet/service-records/${id}`);
  },
  addService(input: CreateServiceInput): Promise<ServiceRecord> {
    return api.post<ServiceRecord>("/fleet/service-records", input);
  },
  setServiceStatus(id: string, input: { status: "IN_SERVICE" | "CANCELLED"; version: number }): Promise<ServiceRecord> {
    return api.post<ServiceRecord>(`/fleet/service-records/${id}/status`, input);
  },
  completeService(id: string, input: CompleteServiceInput): Promise<ServiceRecord> {
    return api.post<ServiceRecord>(`/fleet/service-records/${id}/complete`, input);
  },
  damages(params: Params): Promise<Page<Damage>> {
    return api.page<Damage>("/fleet/damages", { params });
  },
  damage(id: string): Promise<Damage> {
    return api.get<Damage>(`/fleet/damages/${id}`);
  },
  addDamage(input: CreateDamageInput): Promise<Damage> {
    return api.post<Damage>("/fleet/damages", input);
  },
  setDamageStatus(id: string, input: DamageStatusInput): Promise<Damage> {
    return api.post<Damage>(`/fleet/damages/${id}/status`, input);
  },
  /** Hasarı kapat (F-8): onarım bedeli/günü, sigorta tazminatı; net bedel masraf defterine. */
  closeDamage(id: string, input: CloseDamageInput): Promise<Damage> {
    return api.post<Damage>(`/fleet/damages/${id}/close`, input);
  },
};

/** Dosyadan içe aktarma. */
export const fleetImportApi = {
  imports(params: Params): Promise<Page<ImportBatch>> {
    return api.page<ImportBatch>("/fleet/imports", { params });
  },
  importPreview(input: ImportPreviewInput): Promise<ImportPreview> {
    // Ön izleme bir OKUMA: hiçbir şey yazmıyor, ekranı tazelemesin.
    return api.post<ImportPreview>("/fleet/imports/preview", input, { quiet: true });
  },
  runImport(input: CreateImportInput): Promise<ImportBatch> {
    return api.post<ImportBatch>("/fleet/imports", input);
  },
};

/** Dış sistem bağlantıları (`fleet.integration.*`). */
export const fleetIntegrationApi = {
  providers(): Promise<ProviderInfo[]> {
    return api.get<ProviderInfo[]>("/fleet/integrations/providers");
  },
  connections(params: Params): Promise<Page<Connection>> {
    return api.page<Connection>("/fleet/integrations/connections", { params });
  },
  createConnection(input: CreateConnectionInput): Promise<Connection> {
    return api.post<Connection>("/fleet/integrations/connections", input);
  },
  updateConnection(id: string, input: UpdateConnectionInput): Promise<Connection> {
    return api.patch<Connection>(`/fleet/integrations/connections/${id}`, input);
  },
  testConnection(id: string): Promise<ConnectionTest> {
    return api.post<ConnectionTest>(`/fleet/integrations/connections/${id}/test`, {});
  },
};

/** Fotoğraftan okuma. */
export const fleetRecognitionApi = {
  recognize(input: { fileId: string; kind: RecognitionKind; legalEntityId?: string | null }): Promise<Recognition> {
    return api.post<Recognition>("/fleet/recognitions", input);
  },
  recognitionFeedback(id: string, input: { acceptedFields: string[]; correctedFields: Record<string, string>; fleetVehicleId?: string | null }): Promise<Recognition> {
    return api.post<Recognition>(`/fleet/recognitions/${id}/feedback`, input);
  },
};

/** Konum — KRİTİK `fleet.location.read`; her okuma sunucuda kayda geçer. */
export const fleetLocationApi = {
  /** `fleetVehicleId` (31 Ekim 2026, F-A13): tek aracın son konumu — sunucu yalnız onu okur ve kaydeder. */
  locations(legalEntityId: string | null, fleetVehicleId?: string): Promise<LastPosition[]> {
    return api.get<LastPosition[]>("/fleet/locations", { params: { legalEntityId, ...(fleetVehicleId ? { fleetVehicleId } : {}) } });
  },
  track(id: string, params: { from: string; to: string }): Promise<TrackPoint[]> {
    return api.get<TrackPoint[]>(`/fleet/vehicles/${id}/track`, { params });
  },
  mapConfig(): Promise<MapConfig> {
    return api.get<MapConfig>("/fleet/map/config");
  },
};

/**
 * Maliyet raporunun dışa aktarımı — raporlama modülünün genel ihraç hattı (`report.fleet-costs`).
 * İki anahtar: `reporting.report.export` VE raporun kendi okuması `fleet.cost.read`. Dosya işçide
 * (5 dakikada bir) üretilir; istek yalnız sıraya alır.
 */
export const fleetCostExportApi = {
  create(input: CreateReportExportInput): Promise<ReportExportJob> {
    return api.post<ReportExportJob>("/reporting/fleet-costs/export", input);
  },
  list(): Promise<Page<ReportExportJob>> {
    return api.page<ReportExportJob>("/reporting/exports", { params: { report: "fleet-costs", limit: 5, sortBy: "createdAt", sortOrder: "desc" } });
  },
};

/** Ekler (dosya hattı + filo bağını kaldırma). */
export const fleetFileApi = {
  attachments(entityType: FleetSubject, entityId: string): Promise<Page<FleetAttachment>> {
    return api.page<FleetAttachment>("/files/links", { params: { entityType, entityId, limit: 100 } });
  },
  purposes(entityType: FleetSubject | "fleet.import_batch"): Promise<AttachmentPurpose[]> {
    return api.get<AttachmentPurpose[]>("/files/purposes", { params: { entityType } });
  },
  uploadIntent(input: { filename: string; contentType: string; byteSize: number; purpose: string; entityType: string; entityId?: string }): Promise<UploadIntent> {
    return api.post<UploadIntent>("/files/upload-intent", input);
  },
  completeUpload(fileId: string): Promise<unknown> {
    return api.post(`/files/${fileId}/complete`, {});
  },
  link(input: { fileId: string; entityType: FleetSubject; entityId: string; purpose: string }): Promise<unknown> {
    return api.post("/files/links", input);
  },
  unlink(linkId: string, reason: string): Promise<void> {
    return api.post(`/fleet/attachments/${linkId}/unlink`, { reason });
  },
  downloadUrl(fileId: string): Promise<{ url: string }> {
    return api.get<{ url: string }>(`/files/${fileId}/download`);
  },
};
